import React, { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, LogIn, ShieldCheck, TrendingUp, Zap } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/auth/callback";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const fillDemo = (role) => {
    if (role === "admin") {
      setEmail("admin@tcpanalytical.com");
      setPassword("HoneyBadger2026!");
    } else if (role === "exec") {
      setEmail("exec@tcpanalytical.com");
      setPassword("ExecDemo2026!");
    } else {
      setEmail("manager@cedarlane.com");
      setPassword("Manager2026!");
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row" data-testid="login-page">
      <Toaster position="top-right" richColors />

      {/* Left — brand / story panel */}
      <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-between overflow-hidden bg-navy-600 p-12 text-white">
        <div
          className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-orange/20 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="absolute -left-16 bottom-0 h-72 w-72 rounded-full bg-brand-orange/10 blur-3xl"
          aria-hidden="true"
        />

        <div className="relative flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-brand-orange">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display text-sm font-semibold tracking-widest">
              HONEY BADGER
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
              TCP Analytical · Enterprise BI
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
            Project Honey Badger
          </div>
          <h1 className="mt-3 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            The single source of truth for{" "}
            <span className="text-brand-orange">eleven companies</span> across{" "}
            <span className="text-brand-orange">seven ERPs</span>.
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-300">
            Centralize Chempax, Syspro, Infor, Sage, QuickBooks, Misys, and
            Smartsheet data into one normalized, VC-ready financial lake.
            Dashboards, reporting, and data governance — without the manual
            Excel grind.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
            <Stat icon={TrendingUp} label="ERPs unified" value="7" />
            <Stat icon={ShieldCheck} label="Companies" value="11" />
            <Stat icon={Zap} label="SSOT" value="1" />
          </div>
        </div>

        <div className="relative text-xs text-slate-400">
          © {new Date().getFullYear()} TCP Analytical. All rights reserved.
        </div>
      </div>

      {/* Right — auth form */}
      <div className="flex flex-1 items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-navy-600 text-white">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-sm font-semibold tracking-widest text-navy-600">
                HONEY BADGER
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                TCP Analytical
              </div>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-orange">
              Sign in
            </div>
            <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight text-navy-600">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Use your TCP Analytical credentials or continue with Google.
            </p>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 block h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none transition-colors focus:border-navy-600 focus:ring-1 focus:ring-navy-600"
                placeholder="you@tcpanalytical.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                Password
              </label>
              <div className="relative mt-2">
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="block h-11 w-full rounded-sm border border-slate-200 bg-white px-3 pr-10 text-sm outline-none transition-colors focus:border-navy-600 focus:ring-1 focus:ring-navy-600"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-slate-400 hover:text-navy-600"
                  data-testid="login-toggle-password"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-sm bg-brand-orange text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#C1701C] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <LogIn className="h-4 w-4" />
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              or
            </span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={onGoogleSignIn}
            data-testid="login-google-button"
            className="flex h-11 w-full items-center justify-center gap-3 rounded-sm border border-slate-200 bg-white text-sm font-medium text-slate-700 transition-colors hover:border-navy-600"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          {/* Demo helper */}
          <div className="mt-8 rounded-sm border border-dashed border-slate-200 bg-slate-50 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Demo accounts
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {["admin", "exec", "manager"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => fillDemo(r)}
                  data-testid={`demo-${r}`}
                  className="rounded-sm border border-slate-200 bg-white px-2 py-1.5 text-xs capitalize text-slate-600 transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  {r === "manager" ? "Site Manager" : r}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
      <div className="flex items-center gap-2 text-brand-orange">
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-2 font-display text-2xl font-semibold text-white tabular">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">
        {label}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 7.5-11.3 7.5-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.3 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.3 29.5 3 24 3 16.3 3 9.6 7.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5.3 0 10.1-2 13.7-5.3l-6.3-5.2c-2 1.5-4.6 2.5-7.4 2.5-5.4 0-9.9-3.4-11.5-8l-6.6 5.1C9.4 40.5 16.2 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7.1 0 6.4 5.2 6.3 5.2 4.2-3.8 6.9-9.5 6.9-15.9 0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
